import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCornTechnologies } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import { MuiAutocomplete } from '/@/mui/autocomplete';
import { getSessionBrands, getSessionCountry } from '/@/store/session/selectors';

interface TechnologySelectorProps {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    disabled: boolean;
    error?: string;
    /** Skips the internal label — for callers rendering their own (e.g. an inline row layout). */
    hideLabel?: boolean;
    /** Technologies associated with the selected product; used as the options when present. */
    productTechnologies?: { id: number; name?: string; displayInCatalog?: boolean }[];
}

const TechnologySelector = ({
    value,
    onChange,
    disabled,
    error,
    hideLabel,
    productTechnologies,
}: TechnologySelectorProps) => {
    const country = useSelector(getSessionCountry);
    const sessionBrands = useSelector(getSessionBrands);
    const cornBrandCodenames = useMemo(
        () =>
            (sessionBrands.corn || [])
                .map((brand) => brand.codename)
                .filter(Boolean)
                .join(','),
        [sessionBrands.corn]
    );

    const technologiesQuery = useCornTechnologies({
        page: 1,
        pageSize: 10000,
        brand: cornBrandCodenames,
        country: country?.code,
    });

    const technologyOptions = useMemo(() => {
        const catalog = (technologiesQuery.data?.results || []).filter((t: any) => t.displayInCatalog);
        if (!productTechnologies?.length) return catalog;
        // Show the product's own technologies, hiding only the ones explicitly marked as
        // not in the catalog (displayInCatalog === false). If that leaves none available,
        // fall back to the full catalog so the form is never blocked.
        const available = productTechnologies.filter((t) => t.displayInCatalog !== false);
        return available.length ? available : catalog;
    }, [technologiesQuery.data?.results, productTechnologies]);

    return (
        <Stack spacing={0.5} sx={{ width: '100%' }}>
            {!hideLabel && (
                <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                    {_('Tecnología')}
                </Typography>
            )}
            <MuiAutocomplete
                sx={{
                    background: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                        top: '-10px',
                    },
                }}
                disableClearable
                placeholder={_('Indicá la tecnología')}
                disabled={technologiesQuery.isValidating || disabled}
                value={value}
                onChange={(_: any, option: any) => onChange(option?.id)}
                options={technologyOptions}
                loading={technologiesQuery.isValidating}
                getOptionLabel={(option: any) => option.name || ''}
                getOptionValue={(option: any) => option.id}
                error={!!error}
                helperText={error}
                data-testid='field-monitoring-creation-dialog-technology-selector'
            />
        </Stack>
    );
};

export default TechnologySelector;
